import { useGameContext } from "GameContext";
import {
  CLEARINGCOSTS,
  WATER_LEVEL_MANAGEMENT_SCHEMES,
  getWMRIndexFromArray,
} from "utils/simulator";
import {
  mapNumberToColor,
  getCellFaunaCapacity,
} from "utils/game_utils";
import { numToLandType } from "utils/game-information-constants"
import {
  useMemo,
} from "react";


export default function GridDiv({
  view="land",
  cellData=[],
  gameState,
  currentYear=1,
  mapActions={},
  muActions={},
  faunaActions={},
  currentSeason=1,

  showRight=true,
  showLeft=true,

  showFauna = false,

  showAnimals=false,
  showDucksOnly=false,
  selectedWM=-1,

  showOldMU=false,
  oldMuActions={},
  prevGameState=null,
  limitToPersonal=false,
}: {
  currentYear?: number;
  mapActions?: Record<string, any>,
  muActions?: Record<string, any>,
  faunaActions?: Record<string, any>,
  currentSeason?: number;
  view?: string;
  cellData?: Array<any>;
  gameState?: any,
  selectedWM?: number,

  showFauna?: boolean,
  showAnimals?: boolean,
  showDucksOnly?: boolean,

  showRight?: boolean,
  showLeft?: boolean,
  showOldMU?: boolean,
  oldMuActions?: Record<string, any>,
  prevGameState?: any,
  limitToPersonal?: boolean,
}) {
  const { playerInfo } = useGameContext();

  const mapActionImages = {
    "clearing": "/ActionIcons/clearing.png",
    "cut": "/ActionIcons/Cut-Reed.png",
    "grazing": "/ActionIcons/Graze.png",
    "private-hunt": "/ActionIcons/Hunt.png",
    "public-hunt": "/ActionIcons/Hunt.png",
  };

  const ratio = showRight && showLeft ? 1 : 0.5

  function getCurrentEmbank(mu: number) {
    if (showOldMU) {
      let oldMU = null
      if (prevGameState?.muData[mu]?.embanked) {
        oldMU = getWMRIndexFromArray(prevGameState?.muData[mu]?.wmRegime)
      } else {
        oldMU = getWMRIndexFromArray(oldMuActions[`${mu}`]?.wmRegime)
      }
      if (oldMU !== null) {
        return oldMU +1
      }
      return getWMRIndexFromArray(oldMuActions[`${mu}`]?.wmRegime)
    }


    const currentRegime = gameState?.muData?.[mu]?.wmRegime;
    if (gameState?.muData?.[mu]?.embanked) {
      const regime: number[] = Array.isArray(currentRegime) ? currentRegime : [];
      const index = getWMRIndexFromArray(regime);
      return (index ?? 0) + 1
    }
    const proposedRegime = muActions[`${mu}`]?.wmRegime ?? [];
    const proposedIndex = getWMRIndexFromArray(proposedRegime as number[]);
    return (proposedIndex ?? 0) + 1
  }

  return (
    <div className={`flex flex-row w-fit border w-full`}
      style={{
        aspectRatio: ratio,
      }}
    >
      { cellData.map((cellCol: any[], i: number) => {
        if (!showLeft && i < 5) {
          return null
        }
        
        if (!showRight && i > 5) {
          return null
        }

        const cellColR = [...cellCol].reverse();
        return (
          <div key={i} className="flex flex-col grow">
            { cellColR.map((cell: any, j: number) => {
              const actionImages: string[] = []

              if (showFauna) {
                if (faunaActions[`${cell.x}-${cell.y}`] !== undefined) {
                  actionImages.push( mapActionImages["public-hunt"])
                }
              }
              const mapAction = mapActions[`${cell.x}-${cell.y}`] as any;
              if (mapAction?.actionKind) {
                const imageKey = mapAction.actionKind as keyof typeof mapActionImages;
                const actionImage = mapActionImages[imageKey];
                if (actionImage) {
                  if (limitToPersonal) {
                    if (mapAction.authorRole === playerInfo?.role && playerInfo?.estate === mapAction.authorEstateClass) {
                      actionImages.push(actionImage);
                    }
                    // TODO Maybe draw an unknown to show another player has used it
                  } else {
                    actionImages.push(actionImage);
                  }
                }
              }

              const animals = []
              const animalValues = {
                duck: 0,
                bittern: 0,
                heron: 0,
              }

              if (showAnimals || showDucksOnly) {
                const wmwm = selectedWM > -1 ? selectedWM - 1 : 2
                const currentWM = WATER_LEVEL_MANAGEMENT_SCHEMES[wmwm]

                const duckCapacity = getCellFaunaCapacity(cell.x, cell.y, gameState, currentWM, "DUCK");
                const bitternCapacity = getCellFaunaCapacity(cell.x, cell.y, gameState, currentWM, "BOOMING_BITTERN");
                const heronCapacity = getCellFaunaCapacity(cell.x, cell.y, gameState, currentWM, "PURPLE_HERON_COLONY");

                animalValues.duck += duckCapacity
                animalValues.bittern += bitternCapacity
                animalValues.heron += heronCapacity

                if (duckCapacity > 3) {
                  animals.push("/ActionIcons/duck.png")
                } 

                if (!showDucksOnly) {
                  if (bitternCapacity >= 0.3) {
                    animals.push("/ActionIcons/bittern.png")
                  } 
                  if (heronCapacity > 4) {
                    animals.push("/ActionIcons/Heron.png")
                  }
                }
              }


              let backgroundColor = "#000"

              switch (view) {
                case "water":
                  let useYear = Math.max(currentYear - 1, 1);
                  const yearLog = cell.wlLog.slice( (useYear - 1) * 4,  (useYear - 1) * 4 + 4) 

                  let seasonLog = 0;
                  for (let k=0 ; k < yearLog.length; k++) {
                    if (yearLog[k][1] === currentSeason - 1) {
                      seasonLog = yearLog[k][0];
                      break;
                    }
                  }

                  backgroundColor = mapNumberToColor(
                    seasonLog,
                    0,
                    40,
                    '#62f0ed',
                    '#081a63',
                    '#C79428',
                  );
                  break;
                default:
                  backgroundColor = numToLandType(cell.landType).color;
              }


              let showBorder = (muActions[`${cell.managementUnit}`]?.actionKind === "embank" || (gameState?.muData[cell.managementUnit]?.embanked &&  muActions[`${cell.managementUnit}`]?.actionKind !== "disembank")); // true;

              return (
                <div key={j} className={`flex grow relative`}
                  style={{
                    backgroundColor: backgroundColor,

                    borderTop: showBorder ? "1px dotted black" : "",
                    borderLeft: showBorder ? "1px dotted black" : "",
                    borderRight: showBorder ? "1px dotted black" : "",
                    borderBottom: showBorder ? "1px dotted black" : "",
                  }}
                >
                  {showBorder && (
                    <p className="absolute left-0 top-0 w-full">
                      WM { getCurrentEmbank(cell.managementUnit) }
                    </p>
                  )}

                  <div className="absolute bottom-0 left-0 w-full flex flex-row justify-end">
                    {actionImages.map((action,i) => (
                      <img key={i} className={`flex w-1/3`} src={action}/>
                    ))}
                  </div>

                  <div className="absolute top-0 left-0 w-full flex flex-row justify-end">
                    {animals.map((ani, i) => (
                      <img key={i} className={`flex w-1/4`} src={ani}/>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
